import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import AdminShell from "../components/AdminShell.jsx";
import AdminGalleryManager from "../components/AdminGalleryManager.jsx";
import {
  addUnitToProperty,
  deleteProperty,
  getProperty,
  removeUnitFromProperty,
  updatePropertyAddress,
  updatePropertyDescription,
  updateUnitAvailability,
} from "../admin/properties.js";
import "../admin.css";

const EMPTY_UNIT_FORM = {
  label: "",
  unitNumber: "",
};

function PropertyAdmin() {
  const navigate = useNavigate();
  const { propertySlug } = useParams();
  const propertyId = decodeURIComponent(propertySlug || "");
  const [address, setAddress] = useState("");
  const [addressError, setAddressError] = useState("");
  const [addressStatus, setAddressStatus] = useState("");
  const [addingUnit, setAddingUnit] = useState(false);
  const [deletingProperty, setDeletingProperty] = useState(false);
  const [deletingUnitId, setDeletingUnitId] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState(null);
  const [savingAddress, setSavingAddress] = useState(false);
  const [savingDescription, setSavingDescription] = useState(false);
  const [status, setStatus] = useState("");
  const [togglingUnitId, setTogglingUnitId] = useState("");
  const [unitForm, setUnitForm] = useState(EMPTY_UNIT_FORM);

  useEffect(() => {
    let isMounted = true;

    async function loadPropertyDetails() {
      try {
        setLoading(true);
        const nextProperty = await getProperty(propertyId);

        if (isMounted) {
          setProperty(nextProperty);
        }
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message || "We could not load that property yet.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadPropertyDetails();

    return () => {
      isMounted = false;
    };
  }, [propertyId]);

  useEffect(() => {
    setDescription(property?.description || "");
  }, [property?.description]);

  useEffect(() => {
    setAddress(property?.address || property?.mapQuery || "");
  }, [property?.address, property?.mapQuery]);

  function handleUnitFormChange(event) {
    const { name, value } = event.target;

    setUnitForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }));
    setError("");
    setStatus("");
  }

  async function handleAddUnit(event) {
    event.preventDefault();

    if (!property) {
      return;
    }

    try {
      setAddingUnit(true);
      setError("");
      setStatus("");

      const nextProperty = await addUnitToProperty(property.id, unitForm);

      setProperty(nextProperty);
      setUnitForm(EMPTY_UNIT_FORM);
      setStatus(`${unitForm.label || `Unit ${unitForm.unitNumber}`} is ready for photos.`);
    } catch (addError) {
      setError(addError.message || "We could not add that unit yet.");
    } finally {
      setAddingUnit(false);
    }
  }

  async function handleDeleteUnit(unit) {
    if (!property) {
      return;
    }

    const shouldDelete = window.confirm(
      `Delete ${unit.label} and remove every photo from ${unit.storagePath}?`
    );

    if (!shouldDelete) {
      return;
    }

    try {
      setDeletingUnitId(unit.id);
      setError("");
      setStatus("");

      const nextProperty = await removeUnitFromProperty(property.id, unit.id);

      setProperty(nextProperty);
      setStatus(`${unit.label} was deleted.`);
    } catch (deleteError) {
      setError(deleteError.message || "We could not delete that unit yet.");
    } finally {
      setDeletingUnitId("");
    }
  }

  async function handleDeleteProperty() {
    if (!property) {
      return;
    }

    const shouldDelete = window.confirm(
      `Delete ${property.label}, all of its unit docs, and every photo in ${property.propertyStoragePath}?`
    );

    if (!shouldDelete) {
      return;
    }

    try {
      setDeletingProperty(true);
      setError("");
      setStatus("");

      await deleteProperty(property.id);
      navigate("/admin");
    } catch (deleteError) {
      setError(deleteError.message || "We could not delete that property yet.");
      setDeletingProperty(false);
    }
  }

  async function handleToggleAvailability(unit) {
    if (!property) {
      return;
    }

    try {
      setTogglingUnitId(unit.id);
      setError("");
      setStatus("");

      const nextProperty = await updateUnitAvailability(property.id, unit.id, !unit.available);

      setProperty(nextProperty);
      setStatus(
        `${unit.label} is now marked as ${unit.available ? "unavailable" : "available"}.`
      );
    } catch (toggleError) {
      setError(toggleError.message || "We could not update that unit's availability yet.");
    } finally {
      setTogglingUnitId("");
    }
  }

  async function handleSaveDescription(event) {
    event.preventDefault();

    if (!property) {
      return;
    }

    try {
      setSavingDescription(true);
      setError("");
      setStatus("");

      const nextProperty = await updatePropertyDescription(property.id, description);

      setProperty(nextProperty);
      setStatus("Homepage description updated.");
    } catch (saveError) {
      setError(saveError.message || "We could not save that property description yet.");
    } finally {
      setSavingDescription(false);
    }
  }

  async function handleSaveAddress(event) {
    event.preventDefault();

    if (!property) {
      return;
    }

    try {
      setSavingAddress(true);
      setAddressError("");
      setAddressStatus("");

      const nextProperty = await updatePropertyAddress(property.id, address);

      setProperty(nextProperty);
      setAddressStatus("Property address updated.");
    } catch (saveError) {
      setAddressError(saveError.message || "We could not save that property address yet.");
    } finally {
      setSavingAddress(false);
    }
  }

  if (!loading && !property) {
    return <Navigate replace to="/admin" />;
  }

  return (
    <AdminShell
      backLabel="Dashboard"
      backTo="/admin"
      subtitle={
        property
          ? `Upload or remove the featured ${property.label} slideshow here, then add, remove, or open individual units to manage their galleries.`
          : "Loading property details..."
      }
      title={property ? `${property.label} Admin` : "Property Admin"}
    >
      {loading || !property ? (
        <section className="admin-state-card">
          <p className="admin-state-card__eyebrow">Loading</p>
          <h1>Preparing property tools</h1>
          <p>Please wait while we load the property details.</p>
        </section>
      ) : (
        <>
          <AdminGalleryManager
            description="These are the photos shown on the homepage property card."
            documentPath={property.documentPath}
            emptyMessage="No property-level photos have been uploaded yet."
            heading={`${property.label} Homepage Gallery`}
            storageDirectory={property.propertyStoragePath}
          />

          <section className="admin-card">
            <div className="admin-card__header admin-card__header--stacked">
              <div>
                <p className="admin-page__eyebrow">Homepage Copy</p>
                <h2>Property Description</h2>
              </div>
              <p>
                This short description appears on the homepage under the available-unit count for{" "}
                {property.label}.
              </p>
            </div>

            <form className="admin-grid-form admin-grid-form--tight" onSubmit={handleSaveDescription}>
              <label className="admin-input-group admin-input-group--full">
                <span>Homepage Description</span>
                <textarea
                  name="description"
                  onChange={(event) => {
                    setDescription(event.target.value);
                    setError("");
                    setStatus("");
                  }}
                  placeholder="Add a short description for this property."
                  rows="4"
                  value={description}
                />
              </label>

              <div className="admin-form-actions">
                <button className="btn btn-primary btn-lg" disabled={savingDescription} type="submit">
                  {savingDescription ? "Saving..." : "Save Description"}
                </button>
              </div>
            </form>

            {status ? <p className="admin-feedback admin-feedback--success">{status}</p> : null}
            {error ? <p className="admin-feedback admin-feedback--error">{error}</p> : null}
          </section>

          <section className="admin-card">
            <div className="admin-card__header admin-card__header--stacked">
              <div>
                <p className="admin-page__eyebrow">Property Address</p>
                <h2>Address & Map Location</h2>
              </div>
              <p>
                This address is saved to the property and also controls the Google Map shown beside
                the homepage slideshow for {property.label}.
              </p>
            </div>

            <form className="admin-grid-form admin-grid-form--tight" onSubmit={handleSaveAddress}>
              <label className="admin-input-group admin-input-group--full">
                <span>Address</span>
                <input
                  name="address"
                  onChange={(event) => {
                    setAddress(event.target.value);
                    setAddressError("");
                    setAddressStatus("");
                  }}
                  placeholder="123 Main Street, Wolfville, Nova Scotia"
                  type="text"
                  value={address}
                />
              </label>

              <p className="admin-inline-note admin-inline-note--full">
                Tip: use the full civic address when possible so Google Maps places the marker more
                accurately.
              </p>

              <div className="admin-form-actions">
                <button className="btn btn-primary btn-lg" disabled={savingAddress} type="submit">
                  {savingAddress ? "Saving..." : "Save Address"}
                </button>
              </div>
            </form>

            {addressStatus ? <p className="admin-feedback admin-feedback--success">{addressStatus}</p> : null}
            {addressError ? <p className="admin-feedback admin-feedback--error">{addressError}</p> : null}
          </section>

          <section className="admin-card">
            <div className="admin-card__header">
              <div>
                <p className="admin-page__eyebrow">Units</p>
                <h2>Manage Individual Unit Galleries</h2>
                <p>Select a unit to upload or delete the photos shown on its dedicated page.</p>
              </div>
              <div className="admin-card__actions admin-card__actions--compact">
                <Link
                  className="btn btn-outline-dark"
                  to={`/properties/${encodeURIComponent(property.id)}`}
                >
                  View Public Page
                </Link>
                <button
                  className="admin-delete-button"
                  disabled={deletingProperty}
                  onClick={handleDeleteProperty}
                  type="button"
                >
                  {deletingProperty ? "Deleting Property..." : "Delete Property"}
                </button>
              </div>
            </div>

            <form className="admin-grid-form admin-grid-form--tight" onSubmit={handleAddUnit}>
              <label className="admin-input-group">
                <span>Unit Number</span>
                <input
                  name="unitNumber"
                  onChange={handleUnitFormChange}
                  placeholder="2"
                  type="text"
                  value={unitForm.unitNumber}
                />
              </label>

              <label className="admin-input-group">
                <span>Unit Label</span>
                <input
                  name="label"
                  onChange={handleUnitFormChange}
                  placeholder="Unit 2 - 6 Bedroom"
                  type="text"
                  value={unitForm.label}
                />
              </label>

              <div className="admin-form-actions">
                <button className="btn btn-primary btn-lg" disabled={addingUnit} type="submit">
                  {addingUnit ? "Adding..." : "Add Unit"}
                </button>
              </div>
            </form>

            <div className="admin-grid admin-grid--compact">
              {property.units.map((unit) => (
                <article className="admin-unit-card" key={unit.id}>
                  <div className="admin-unit-card__copy">
                    <h3>{unit.label}</h3>
                    <div className="admin-unit-card__meta">
                      <span
                        className={`admin-badge ${
                          unit.available
                            ? "admin-badge--success"
                            : unit.availabilityStatusClass === "scheduled"
                              ? "admin-badge--scheduled"
                              : "admin-badge--muted"
                        }`}
                      >
                        {unit.availabilityStatusText}
                      </span>
                    </div>
                    <p>Firestore: {unit.documentPath}</p>
                    <p>Storage: {unit.storagePath}</p>
                  </div>
                  <div className="admin-unit-card__actions">
                    <Link
                      className="btn btn-outline-dark"
                      to={`/admin/${encodeURIComponent(property.id)}/units/${encodeURIComponent(unit.id)}`}
                    >
                      Open {unit.label}
                    </Link>
                    <button
                      className="btn btn-outline-dark"
                      disabled={togglingUnitId === unit.id}
                      onClick={() => handleToggleAvailability(unit)}
                      type="button"
                    >
                      {togglingUnitId === unit.id
                        ? "Saving..."
                        : unit.available
                          ? "Mark Unavailable"
                          : "Mark Available"}
                    </button>
                    <button
                      className="admin-delete-button"
                      disabled={deletingUnitId === unit.id || togglingUnitId === unit.id}
                      onClick={() => handleDeleteUnit(unit)}
                      type="button"
                    >
                      {deletingUnitId === unit.id ? "Deleting..." : "Delete Unit"}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </AdminShell>
  );
}

export default PropertyAdmin;
